import React from 'react';
import { Check, Star } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const Pricing = () => {
  const plans = [
    {
      name: 'Starter MVP',
      price: '$2,499',
      description: 'For small projects',
      tagline: 'Rapid prototyping and validation',
      popular: false,
      features: [
        '1 core feature',
        'Landing page',
        'Deployment included',
        'Basic UI/UX design',
        '2 weeks delivery',
        'Email support'
      ]
    },
    {
      name: 'Growth MVP',
      price: '$4,999',
      description: 'For MVP launch',
      tagline: 'Your dedicated technical team',
      popular: true,
      features: [
        '3–5 core features',
        'API integration',
        'Branding & UX',
        'Database setup',
        '3 weeks delivery',
        'Priority support',
        'Post-launch support (1 month)'
      ]
    },
    {
      name: 'Full Launch',
      price: '$7,999+',
      description: 'Partner',
      tagline: 'We scale your company together',
      popular: false,
      features: [
        'Full SaaS product',
        'AI features included',
        'Dedicated dev team',
        'Advanced integrations',
        'Custom timeline',
        '24/7 support',
        'Ongoing maintenance',
        'Technical consulting'
      ]
    }
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="pricing" className="py-24 px-6 relative bg-black">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-white/60 font-semibold text-sm uppercase tracking-wide">Pricing</span>
          <h2 className="text-4xl md:text-5xl font-extrabold mt-4 mb-4 text-white">
            Prices That Make Sense!
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">Transparent, fixed pricing with no hidden costs</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <Card
              key={index}
              className={`relative ${
                plan.popular
                  ? 'bg-white/10 border-white/40 transform scale-105'
                  : 'bg-white/5 border-white/10'
              } hover:border-white/30 transition-all duration-300 hover:transform hover:-translate-y-2 backdrop-blur-sm`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-white text-black font-bold px-4 py-1 shadow-lg">
                    <Star className="w-3 h-3 mr-1 inline" />
                    Best Value
                  </Badge>
                </div>
              )}
              <CardContent className="p-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-gray-400 text-sm mb-4">{plan.tagline}</p>
                  <div className="flex items-baseline mb-2">
                    <span className="text-5xl font-extrabold text-white">{plan.price}</span>
                    {plan.price.includes('$') && !plan.price.includes('+') && (
                      <span className="text-gray-400 ml-2">fixed</span>
                    )}
                  </div>
                  <p className="text-gray-500 text-sm">{plan.description}</p>
                </div>

                <Button
                  className={`w-full mb-6 ${
                    plan.popular
                      ? 'bg-white hover:bg-gray-100 text-black'
                      : 'bg-transparent border-2 border-white/30 text-white hover:bg-white/10'
                  } font-bold transition-all duration-300`}
                  onClick={() => window.open('https://calendly.com/rajvverma2000/30min', '_blank')}
                >
                  {plan.popular ? 'Book a Call' : 'Schedule Call'}
                </Button>

                <div className="space-y-3">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start">
                      <Check className="w-5 h-5 text-white mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;